/**
 * 怪兽图鉴 / 圈养园
 * - 最多 4 只怪，按入园时间 FIFO
 * - 按体型自动分配 biome（水/空/地）
 */

import type { BodyType } from "./spriteLib";
import type { Screen2Combo } from "./screens/screen2";
import type { MonsterDesc } from "../shared/types";

const KEY = "zooEntries";
const MAX = 4;

export type Biome = "water" | "air" | "ground";

export interface ZooEntry {
  scpId: string;
  monster: MonsterDesc;
  combo: Screen2Combo;
  originalName: string;
  ts: number;
}

function read(): ZooEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.slice(0, MAX);
  } catch {
    return [];
  }
}

function write(entries: ZooEntry[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries.slice(0, MAX)));
  } catch {
    /* quota / disabled */
  }
}

export function getZoo(): ZooEntry[] {
  return read();
}

export function addToZoo(monster: MonsterDesc, combo: Screen2Combo, originalName: string): void {
  const list = read();
  // 去重 by scpId（同一只不能入园两次）
  const filtered = list.filter((e) => e.scpId !== monster.scpId);
  filtered.unshift({
    scpId: monster.scpId,
    monster,
    combo,
    originalName,
    ts: Date.now(),
  });
  write(filtered);
}

export function clearZoo(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * 按体型自动分到 biome
 *  - fish → water
 *  - winged / abstract / snake / tall / crystal → air
 *  - 其余 → ground
 */
export function getBiome(bodyType: BodyType): Biome {
  switch (bodyType) {
    case "fish":
      return "water";
    case "winged":
    case "abstract":
    case "snake":
    case "tall":
    case "crystal":
      return "air";
    default:
      return "ground";
  }
}

export const BIOME_LABELS: Record<Biome, string> = {
  water: "水域",
  air: "空域",
  ground: "陆地",
};

export const BIOME_COLORS: Record<Biome, { main: string; sub: string; accent: string; text: string }> = {
  water: { main: "#2a5d8f", sub: "#4a8db8", accent: "#6baad8", text: "#88c8e8" },
  air:   { main: "#3a5a7a", sub: "#5a7a9a", accent: "#a0c0e0", text: "#c0e0f0" },
  ground:{ main: "#3a2a1a", sub: "#5a3a2a", accent: "#7a5a4a", text: "#aa8a6a" },
};
